import React, { useState } from 'react';
import { CaretDown, CaretUp, Calculator } from '@phosphor-icons/react';

/**
 * Reusable Formula Explainer card — shows on top of W2 and W3.1.
 * Default collapsed. Expanded shows full math + worked example.
 */
export default function FormulaExplainer({ context = 'sum-range' }) {
  const [open, setOpen] = useState(false);
  const isSumRange = context === 'sum-range';
  const isSevenCombo = context === 'seven-combo';
  const isBallDiff = context === 'ball-diff';
  let exampleLabel, exF1, exF2;
  if (isBallDiff) {
    exampleLabel = 'Ball 1 · Diff −2';
    exF1 = '9.5 × (1.85)^0.5 ≈ 12.92 × vol × recency × consec = 7.22';
    exF2 = 'T:6.9 + G:18.5 + Y:2.4 + B:14.25 + DoW:0.5 + M2:0.5 ≈ 43.05';
  } else if (isSevenCombo) {
    exampleLabel = '7-Combo DOWN×7';
    exF1 = '3.56 × (1.45)^0.5 ≈ 4.29';
    exF2 = 'T:0 + G:14.5 + Y:7.1 + B:5.34 + DoW:0 + M2:0 ≈ 27.0';
  } else {
    exampleLabel = isSumRange ? 'Sum Range 130-139' : 'Sign Pattern ▼▼▼▼▼ (-----)';
    exF1 = isSumRange ? '12.8 × (2.31)^0.5 = 19.46' : '12.6 × (2.88)^0.5 = 21.39';
    exF2 = isSumRange
      ? 'T:6.9 (23×0.3)  +  G:23.1 (min(2.31×10, 30))  +  Y:13.2 (max(6.6×2, 0))  +  B:19.2 (12.8×1.5)  +  DoW:0.98  =  63.4'
      : 'T:9.0 (30×0.3)  +  G:28.8 (min(2.88×10, 30))  +  Y:8.4 (max(4.2×2, 0))  +  B:18.9 (12.6×1.5)  +  DoW:1.2  =  66.3';
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-200 rounded-xl mb-6 overflow-hidden" data-testid="formula-explainer">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-indigo-100/40 transition-colors"
        data-testid="formula-explainer-toggle"
      >
        <div className="flex items-center gap-2">
          <Calculator size={20} className="text-indigo-700" />
          <span className="font-bold text-indigo-900">How are the scores calculated?</span>
          <span className="text-[11px] text-indigo-600 font-mono">(click to {open ? 'hide' : 'show'})</span>
        </div>
        {open ? <CaretUp size={18} className="text-indigo-700" /> : <CaretDown size={18} className="text-indigo-700" />}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-2 space-y-4">
          {/* Side-by-side formulas */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Formula 1 */}
            <div className="bg-white border-2 border-blue-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-blue-600 text-white text-xs font-black px-2 py-0.5 rounded">FORMULA 1</span>
                <span className="text-sm font-bold text-blue-900">Long-Term · Multiplicative</span>
              </div>
              <p className="text-xs text-gray-600 mb-3">Identifies items that are statistically due based on long-term history.</p>
              <div className="bg-blue-50 rounded p-3 font-mono text-sm text-blue-900 mb-2">
                pred_score = freq% × (overdue_ratio)^0.5
              </div>
              <p className="text-[11px] text-emerald-700 mb-2">
                <strong>Why ^0.5?</strong> Backtest on the last 50 draws showed ^0.5 hits 34 % top-3 vs 30 % at ^0.7 (+4 pp gain). Auto-tuned 2026-04-30.
              </p>
              <p className="text-[11px] text-gray-600 mb-2"><strong>+ Volatility Penalty:</strong> divides score by (1 + cv × 0.5) where cv = gap_std / mean_gap.</p>
              <p className="text-[11px] text-gray-600 mb-2"><strong>+ Recency Factor (0.85–1.25):</strong> exponentially-weighted recent-gaps trend ratio. Boosts ranges whose recent gaps are growing (getting starved).</p>
              <p className="text-[11px] text-gray-600 mb-2"><strong>+ Consecutive-Miss Bonus:</strong> +5 % at 1.5× overdue, +12 % at 2×, +20 % at 2.5×.</p>
              <div className="text-[11px] bg-emerald-50 border-l-2 border-emerald-400 p-2">
                <strong>Example ({exampleLabel}):</strong> {exF1}
              </div>
            </div>

            {/* Formula 2 */}
            <div className="bg-white border-2 border-orange-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-orange-600 text-white text-xs font-black px-2 py-0.5 rounded">FORMULA 2</span>
                <span className="text-sm font-bold text-orange-900">Next-Draw · Additive (4-component)</span>
              </div>
              <p className="text-xs text-gray-600 mb-3">Predicts what's most likely on the very next draw using contextual signals.</p>
              <div className="bg-orange-50 rounded p-3 font-mono text-[11px] text-orange-900 space-y-1 mb-2">
                <div>T (1st-order Trans) = trans_pct × 0.3</div>
                <div>G (Gap) = min(overdue × 10, 30)</div>
                <div>Y (Year) = max(−year_diff × 2, 0) capped 20</div>
                <div>B (Base) = freq% × 1.5</div>
                <div>DoW = max(0, (dow% − 50) × 0.1)</div>
                <div>M2 (2nd-order Markov) = pair_pct × 0.2</div>
                <div className="font-bold border-t border-orange-300 pt-1 mt-1">combined = T + G + Y + B + DoW + M2</div>
              </div>
              <div className="text-[11px] bg-emerald-50 border-l-2 border-emerald-400 p-2">
                <strong>Example ({exampleLabel}):</strong> {exF2}
              </div>
            </div>
          </div>

          {/* Convergence explainer */}
          <div className="bg-white border-2 border-fuchsia-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-fuchsia-600 text-white text-xs font-black px-2 py-0.5 rounded">CONVERGENCE</span>
              <span className="text-sm font-bold text-fuchsia-900">Cross-Formula Confirmation</span>
            </div>
            <p className="text-xs text-gray-600 mb-2">Two independent formulas agreeing on the same item = much stronger signal than either alone.</p>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="bg-amber-50 border border-amber-300 rounded p-2"><strong>🥇 ELITE</strong><br/>Top-3 in BOTH F1 & F2</div>
              <div className="bg-blue-50 border border-blue-300 rounded p-2"><strong>🥈 STRONG</strong><br/>Top-3 in one + Top-5 in other</div>
              <div className="bg-gray-50 border border-gray-300 rounded p-2"><strong>🥉 MODERATE</strong><br/>Top-5 in either</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
